import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react'
import { addMonths, endOfMonth, startOfMonth } from 'lib/datetime/dateUtils'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import GeoContext from 'contexts/geoContext'

import moment from 'moment'
import FlightsDatePickerDay from './FlightsDatePickerDay'
import BodyText from 'components/Luxkit/Typography/BodyText'
import { FlightsFareTypes } from 'constants/flight'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchFlightCalendar, getFlightCalendarKey } from 'actions/FlightActions'
import { fireInteractionEvent } from 'api/googleTagManager'
import { flightsArrivalDateSelect, flightsDepartureDateSelect } from 'analytics/eventDefinitions'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import DatePicker from 'components/Common/Calendar/DatePicker'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import HiddenInput from 'components/Common/Form/Input/HiddenInput'

interface Props {
  minDate?: Date;
  maxDate?: Date;
  className?: string;
  type: 'single' | 'dual';
}

function FlightsDatePicker(props: Props) {
  const {
    minDate,
    maxDate,
    className,
    type,
  } = props

  const {
    checkinDate: startDate,
    checkoutDate: endDate,
    departureAirport,
    arrivalAirport,
    fareType,
  } = useContext(FlightSearchWidgetStateContext)
  const searchDispatch = useContext(FlightSearchWidgetDispatchContext)

  const { currentRegionCode, currentCurrency } = useContext(GeoContext)

  const [date, setDate] = useState<Date>(startDate?.toDate() ?? new Date())
  const startDateQuery = useMemo(() => moment(startOfMonth(date)).format(ISO_DATE_FORMAT), [date])
  const endDateQuery = useMemo(() => moment(endOfMonth(addMonths(date, 1))).format(ISO_DATE_FORMAT), [date])

  const cacheKey = getFlightCalendarKey({
    startDate: startDateQuery,
    endDate: endDateQuery,
    origin: departureAirport?.airportCode ?? '',
    destination: arrivalAirport?.airportCode ?? '',
    region: currentRegionCode,
    currency: currentCurrency,
  })

  const cachedPrices = useAppSelector(state => state.flights.flightCalendar[cacheKey])
  const loading = useAppSelector(state => state.flights.flightCalendarLoading[cacheKey])

  const dispatch = useAppDispatch()

  useEffect(() => {
    if (departureAirport && arrivalAirport) {
      dispatch(fetchFlightCalendar({
        startDate: startDateQuery,
        endDate: endDateQuery,
        origin: departureAirport.airportCode,
        destination: arrivalAirport.airportCode,
      }))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cacheKey])

  const onDayClick = useCallback((selectedDay: Date) => {
    const day = moment(selectedDay)

    if (fareType?.value === FlightsFareTypes.ONE_WAY) {
      searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: day })
      fireInteractionEvent(flightsDepartureDateSelect())
      searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu: undefined })
    } else {
      if (startDate && !endDate && day.isSameOrAfter(startDate)) {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: startDate })
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: day })
        fireInteractionEvent(flightsArrivalDateSelect())
        searchDispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, activeMenu: undefined })
      } else {
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKIN_DATE, date: day })
        searchDispatch({ type: FlightSearchWidgetActions.SET_CHECKOUT_DATE, date: null })
        fireInteractionEvent(flightsDepartureDateSelect())
      }
    }
  }, [fareType?.value, searchDispatch, startDate, endDate])

  const onMonthChange = useCallback((direction, date: Date) => {
    setDate(date)
  }, [])

  return (
    <VerticalSpacer gap={24} className={className}>
      <HiddenInput name="startDate" value={startDate?.format(ISO_DATE_FORMAT)} />
      <HiddenInput name="endDate" value={endDate?.format(ISO_DATE_FORMAT)} />
      <DatePicker
        min={minDate}
        max={maxDate}
        onMonthChange={onMonthChange}
        defaultMonth={date.getMonth()}
        defaultYear={date.getFullYear()}
        type={type}
        dayRender={(day: Date, params) => {
          const { disabled } = params
          const dayTime = day.getTime()
          const dateOfDay = moment(day)
          const selected = startDate?.valueOf() === dayTime || endDate?.valueOf() === dayTime
          const cachedPrice = cachedPrices?.[dateOfDay.format(ISO_DATE_FORMAT)]?.cost
          const isInRange = (!selected && startDate && endDate) ? dateOfDay.isAfter(startDate) && dateOfDay.isBefore(endDate) : false
          const dataTestId = `day-${dateOfDay.format('YYYY-MM-DD')}`

          return <FlightsDatePickerDay
            key={dayTime}
            day={day}
            dataTestId={dataTestId}
            selected={selected}
            disabled={disabled}
            cachedPrice={cachedPrice}
            loading={loading}
            isInRange={isInRange}
            noCachePrices={!cachedPrices}
            onClick={onDayClick}
          />
        }}
      />
      {cachedPrices && <BodyText variant="small" align="end" italics>
        Recent return prices per adult
      </BodyText>}
    </VerticalSpacer>
  )
}

export default FlightsDatePicker
